import { useMemo } from "react";

// Imported from the leaf modules, not the entities barrel: the barrel pulls in
// enough of the account UI to close an import cycle back through this form.
import { getIGApiPublishingStatusOn } from "../../../../entities/accounts/libs/get-account-statuses";
import { isInstagramAccount } from "../../../../entities/accounts/libs/account-platform";
import type { AccountType } from "../../../../shared/types/accounts";
import { useInstagramApiPublishingOn } from "../../../../utils/feature-toggle-utils";

/*
 * Both library functions take the full `AccountType` but read only the fields
 * named on `TagCapabilityAccount` below. One cast, in one place, rather than at
 * each call, so the fields this component depends on stay visible in its own
 * type instead of being erased by `as never`.
 */
const asAccount = (account: TagCapabilityAccount) => account as AccountType;

/**
 * Every field the capability check actually reads, declared rather than
 * implied.
 *
 * This was four optional fields plus two `as never` casts, which typechecked
 * only because the form reducer happens to put whole account records on
 * `post.accounts`. `getIGApiPublishingStatusOn` reads far more than that, so
 * narrowing the runtime data anywhere would have made the check read undefined
 * for every account and told every customer their tags would be dropped, with
 * nothing going red. Spelling the shape out here is what makes that a compile
 * error instead.
 *
 * Not `AccountType` itself: that demands a dozen unrelated fields a caller has
 * no reason to hold, and it does not even declare `name`.
 */
export type TagCapabilityAccount = {
  _id?: string;
  login?: string;
  name?: string;
  platformType?: string;
  instagram_business_account?: { id?: string };
  session?: { scopes?: string[] };
  longSession?: { scopes?: string[] };
  instagramPublishReconnectRequired?: boolean;
  /** `notApproved` keeps an account off the API track. */
  status?: string;
};

/**
 * SKE-4499. The selected Instagram accounts that could publish through the Meta
 * API, which is what the phone-era composer restrictions were protecting.
 *
 * `getIGApiPublishingStatusOn` cannot see the server's rollout allow-list, so a
 * true answer means "eligible as far as the client can tell". Only widen a gate
 * on it, never narrow one.
 *
 * SKE-4538 also needs the accounts themselves, not just whether any exist: the
 * lookup routes take account ids and refuse the whole request if one of them has
 * no usable token, so a caller must be able to send only these.
 */
export const apiCapableInstagramAccounts = (
  accounts: TagCapabilityAccount[] | null | undefined,
  /** SKE-4733. The workspace's API publishing gate; false means none are. */
  apiPublishingOn: boolean
): TagCapabilityAccount[] =>
  (accounts ?? []).filter(
    (account) =>
      isInstagramAccount(asAccount(account)) &&
      getIGApiPublishingStatusOn(asAccount(account), apiPublishingOn)
  );

/**
 * ANY rather than EVERY, matching how tagging a video already behaves: the
 * control opens, and `TagCapabilityWarning` names the accounts that will drop
 * what the customer entered. Requiring every account would instead refuse the
 * whole post because one phone-track account was selected alongside.
 */
export const hasApiCapableInstagramAccount = (
  accounts: TagCapabilityAccount[] | null | undefined,
  apiPublishingOn: boolean
): boolean => apiCapableInstagramAccounts(accounts, apiPublishingOn).length > 0;

/**
 * SKE-4714. EVERY rather than ANY, and at least one: per-story tags on a story
 * set publish only through the API, and the phone app places no tags on a
 * story, so a single phone-track account in the selection keeps the one
 * post-level tagger and `TagCapabilityWarning` names that account.
 */
export const everyInstagramAccountApiCapable = (
  accounts: TagCapabilityAccount[] | null | undefined,
  apiPublishingOn: boolean
): boolean => {
  if (!apiPublishingOn) return false;
  const instagram = (accounts ?? []).filter((account) =>
    isInstagramAccount(asAccount(account))
  );
  return (
    instagram.length > 0 &&
    instagram.every((account) =>
      getIGApiPublishingStatusOn(asAccount(account), apiPublishingOn)
    )
  );
};

interface TagCapabilityWarningProps {
  accounts: TagCapabilityAccount[];
  /** Only videos and stories need the API path to carry a tag. */
  applies: boolean;
}

const label = (account: TagCapabilityAccount) =>
  account.login || account.name || "this account";

/**
 * SKE-4539. Names the selected Instagram accounts whose tags will not be sent.
 *
 * Tagging a video or a story needs the Meta publishing API. An account still on
 * the phone path publishes the post fine and simply drops the tags, which is
 * the worst kind of failure: nothing errors and the customer only finds out by
 * looking at Instagram. So the control stays usable and this says who misses
 * out, rather than the tags vanishing silently for half a selection.
 */
export const TagCapabilityWarning = ({
  accounts,
  applies,
}: TagCapabilityWarningProps) => {
  const apiPublishingOn = useInstagramApiPublishingOn();
  const dropping = useMemo(
    () =>
      (accounts ?? []).filter(
        (account) =>
          isInstagramAccount(asAccount(account)) &&
          !getIGApiPublishingStatusOn(asAccount(account), apiPublishingOn)
      ),
    [accounts, apiPublishingOn]
  );

  /*
   * SKE-4733. Only a workspace on the API track has a MIXED selection to warn
   * about. Without the gate every account is on the phone path, the composer
   * already refuses what the phone cannot carry, and no warning existed then.
   */
  const warn = apiPublishingOn && applies && dropping.length > 0;
  const names = dropping.map(label).join(", ");

  /*
   * The live region is mounted whether or not there is anything to say. A
   * `role="status"` that appears in the same commit as its text is announced
   * inconsistently, and this text only ever appears in response to the
   * customer switching media or turning the post into a story.
   */
  return (
    <div
      role="status"
      aria-live="polite"
      className={warn ? "col-span-2" : "sr-only"}
    >
      {warn && (
        <p
          className="m-0 flex items-start gap-2 rounded-lg bg-yellow-50 px-3 py-2 font-inter text-xs font-medium leading-5 text-yellow-700"
          data-testid="tag-capability-warning"
        >
          <i
            className="fa-solid fa-triangle-exclamation mt-[3px] shrink-0"
            aria-hidden="true"
          />
          <span>
            Tags will not be added for {names}. These accounts publish through
            the Sked Social phone app, which cannot place tags.
          </span>
        </p>
      )}
    </div>
  );
};

export default TagCapabilityWarning;
